import React, { useState, useEffect, useRef, useCallback } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';
import { CLAUDE_HEADERS } from '../../../shared/api';
import { getGameSession, updateGameState } from '../../../shared/dbService';
import MultiplayerInviteModal from '../../../modals/MultiplayerInviteModal';
import MultiplayerResults from '../../../modals/MultiplayerResults';
import TurnBanner from './TurnBanner';

function MemoryGame(props) {
  const { lang, onBack, allUsers, friends, currentUserId, userName, invitePartner, difficulty = 1 } = props;
  const nl = lang === 'nl';
  // difficulty 1=easy (5 pairs), 2=medium (8 pairs), 3=hard (10 pairs)
  const PAIR_COUNT = difficulty >= 3 ? 10 : difficulty >= 2 ? 8 : 5;
  const ALL_EMOJIS = ['🌟', '🦋', '🌙', '🔮', '🌺', '🌊', '🔑', '🎯', '🐉', '🍀', '⚡', '🎪', '🏔️', '🦋', '🎯', '🌈'];
  const EMOJIS = ALL_EMOJIS.slice(0, PAIR_COUNT);

  // ── Core game state ──────────────────────────────────────────────
  const [cards, setCards]                   = useState([]);
  const [flipped, setFlipped]               = useState([]);       // my own in-progress flip
  const [partnerFlipped, setPartnerFlipped] = useState([]);       // partner's visual flip
  const [partnerLast, setPartnerLast]       = useState([]);       // partner's last-flip highlight
  const [matched, setMatched]               = useState([]);
  const [locked, setLocked]                 = useState(false);
  const [myMoves, setMyMoves]               = useState(0);
  const [aiMoves, setAiMoves]               = useState(0);
  const [done, setDone]                     = useState(false);
  const [lastFlipped, setLastFlipped]       = useState([]);

  // ── Multiplayer ──────────────────────────────────────────────────
  const [showInvite, setShowInvite] = useState(false);
  const [partner, setPartner]       = useState(null);
  const [mode, setMode]             = useState('simultaneous');

  // ── Real-friend sync ─────────────────────────────────────────────
  const [myTurn, setMyTurn]             = useState(true);
  const [partnerMoves, setPartnerMoves] = useState(0);
  const [statusMsg, setStatusMsg]       = useState('');
  const [syncReady, setSyncReady]       = useState(false);
  const processedTsRef = useRef(null);
  const pollRef        = useRef(null);
  const aiTimerRef     = useRef(null);
  const matchedRef     = useRef([]);  // shadow ref so we never downgrade matched from server

  const [showResults, setShowResults] = useState(null);
  const [aiComment, setAiComment]     = useState(null);
  const [best, setBest] = useStorage('gm_mem_best', null);

  // ── Derived flags ────────────────────────────────────────────────
  const isRealFriend = !!(partner && !partner.isAI && partner._token);
  const token        = partner?._token || null;
  const amInviter    = partner?._isInviter || false;
  const myRole       = amInviter ? 'inviter' : 'joiner';
  const partnerRole  = amInviter ? 'joiner' : 'inviter';
  const isTurnBased  = partner && mode === 'turnbased';

  // ── Auto-set partner from room invite ────────────────────────────
  useEffect(() => {
    if (invitePartner) {
      setPartner(invitePartner);
      if (invitePartner._roomMode) setMode(invitePartner._roomMode);
    }
  }, [invitePartner]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify GamesScreen when a real partner with a token is active (for voice chat)
  useEffect(() => {
    if (props.onRealPartner) props.onRealPartner(partner && !partner.isAI && partner._token ? partner : null);
  }, [partner]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── AI comment ──────────────────────────────────────────────────
  const getAiComment = async (my, opp) => {
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST', headers: CLAUDE_HEADERS,
        body: JSON.stringify({
          model: 'claude-haiku-4-5-20251001', max_tokens: 80,
          system: 'Kort, warm en motiverend. Max 25 woorden.',
          messages: [{ role: 'user', content: `Memory. Jij: ${my} zetten. Tegenstander: ${opp} zetten. Minder=beter. Geef korte reactie in het ${lang === 'nl' ? 'Nederlands' : 'Engels'}.` }],
        }),
      });
      const d = await res.json();
      setAiComment(d.content?.[0]?.text || null);
    } catch { setAiComment(null); }
  };

  // ── Local start (solo / AI) ──────────────────────────────────────
  const startLocal = useCallback(() => {
    const deck = [...EMOJIS, ...EMOJIS].sort(() => Math.random() - 0.5).map((e, i) => ({ id: i, emoji: e }));
    setCards(deck); setFlipped([]); setMatched([]); setLocked(false);
    setMyMoves(0); setAiMoves(0); setDone(false);
    setMyTurn(true); setStatusMsg(''); setLastFlipped([]);
    setPartnerFlipped([]); setPartnerLast([]);
    setShowResults(null); setAiComment(null);
    matchedRef.current = [];
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Server-sync start (real friend, any mode) ────────────────────
  const startServer = useCallback(async () => {
    if (!token) return;
    setFlipped([]); setMatched([]); setLocked(false);
    setMyMoves(0); setPartnerMoves(0); setDone(false);
    setStatusMsg(''); setLastFlipped([]);
    setPartnerFlipped([]); setPartnerLast([]);
    setShowResults(null); setAiComment(null);
    processedTsRef.current = null;
    matchedRef.current = [];

    if (amInviter) {
      const deck = [...EMOJIS, ...EMOJIS].sort(() => Math.random() - 0.5).map((e, i) => ({ id: i, emoji: e }));
      const initState = {
        type: 'memory', phase: 'playing', turn: 'inviter',
        cards: deck, matched: [],
        inviterMoves: 0, joinerMoves: 0,
        lastAction: null, done: false,
      };
      await updateGameState(token, initState);
      setCards(deck); setMyTurn(true); setSyncReady(true);
    } else {
      setSyncReady(false); // joiner waits for inviter's deck via poll
    }
  }, [token, amInviter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mount: start appropriate mode ───────────────────────────────
  useEffect(() => {
    if (!partner) { startLocal(); return; }
    if (isRealFriend) { startServer(); }  // ALL real-friend modes use server
    else { startLocal(); }
  }, [partner]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    clearTimeout(aiTimerRef.current);
    clearInterval(pollRef.current);
  }, []);

  // ── Polling for real-friend (both simultaneous and turn-based) ───
  const poll = useCallback(async () => {
    if (!token) return;
    const data = await getGameSession(token);
    if (!data?.gameState) return;
    const gs = data.gameState;

    // Joiner: load deck from server
    if (!syncReady && gs.cards?.length) {
      setCards(gs.cards);
      setSyncReady(true);
    }

    // Turn management (turn-based only)
    if (isTurnBased) {
      const isMyTurnNow = gs.turn === myRole;
      setMyTurn(isMyTurnNow);
      setStatusMsg(!isMyTurnNow && !gs.done
        ? (nl ? `${partner?.name} kiest kaarten...` : `${partner?.name} is picking cards...`)
        : '');
    }

    // Partner's move count always from server
    setPartnerMoves(amInviter ? (gs.joinerMoves || 0) : (gs.inviterMoves || 0));

    // Animate partner's last flip if new
    const la = gs.lastAction;
    if (la && la.by === partnerRole && la.ts !== processedTsRef.current) {
      processedTsRef.current = la.ts;
      // Show partner's cards using SEPARATE partnerFlipped state
      setPartnerFlipped([la.idx1, la.idx2]);
      if (la.isMatch) {
        const sm = gs.matched || [];
        if (sm.length > matchedRef.current.length) {
          matchedRef.current = sm;
          setMatched(sm);
        }
        setTimeout(() => {
          setPartnerFlipped([]);
          setPartnerLast([la.idx1, la.idx2]);
          setTimeout(() => setPartnerLast([]), 900);
        }, 700);
      } else {
        setTimeout(() => {
          setPartnerFlipped([]);
          setPartnerLast([la.idx1, la.idx2]);
          setTimeout(() => setPartnerLast([]), 1000);
        }, 800);
      }
    } else {
      // No new partner action — sync matched only if server is ahead (prevents race regression)
      const sm = gs.matched || [];
      if (sm.length > matchedRef.current.length) {
        matchedRef.current = sm;
        setMatched(sm);
      }
    }

    if (gs.done && !showResults) {
      setDone(true);
      const myM = amInviter ? gs.inviterMoves : gs.joinerMoves;
      const ptM = amInviter ? gs.joinerMoves  : gs.inviterMoves;
      getAiComment(myM, ptM);
      setShowResults({ myScore: myM, partnerScore: ptM });
    }
  }, [token, myRole, partnerRole, amInviter, syncReady, showResults, nl, partner?.name, isTurnBased]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll as long as we have a real friend (any mode)
  useEffect(() => {
    if (!isRealFriend) return;
    poll();
    pollRef.current = setInterval(poll, 1500);
    return () => clearInterval(pollRef.current);
  }, [poll, isRealFriend]);

  // ── AI TAKES ITS TURN ────────────────────────────────────────────
  const doAiTurn = useCallback((currentMatched, currentCards) => {
    setMyTurn(false);
    setStatusMsg(nl ? 'AI kiest twee kaarten...' : 'AI is choosing two cards...');
    setFlipped([]);

    const unmatched = currentCards.map((_, i) => i).filter(i => !currentMatched.includes(currentCards[i].emoji));
    let first, second, foundPair = false;
    if (Math.random() < 0.55) {
      outer: for (let i = 0; i < unmatched.length; i++) {
        for (let j = i + 1; j < unmatched.length; j++) {
          if (currentCards[unmatched[i]].emoji === currentCards[unmatched[j]].emoji) {
            first = unmatched[i]; second = unmatched[j]; foundPair = true; break outer;
          }
        }
      }
    }
    if (!foundPair) {
      const sh = [...unmatched].sort(() => Math.random() - 0.5);
      first = sh[0]; second = sh[1];
    }

    aiTimerRef.current = setTimeout(() => {
      setStatusMsg(nl ? 'AI draait eerste kaart om...' : 'AI flips first card...');
      setFlipped([first]);
      aiTimerRef.current = setTimeout(() => {
        setFlipped([first, second]);
        const isMatch = currentCards[first].emoji === currentCards[second].emoji;
        aiTimerRef.current = setTimeout(() => {
          setAiMoves(m => {
            const nm = m + 1;
            if (isMatch) {
              const nm2 = [...currentMatched, currentCards[first].emoji];
              setMatched(nm2);
              setStatusMsg(nl ? 'AI vindt een paar! 🎉' : 'AI finds a match! 🎉');
              saveGameScore('memory', Math.max(1, 100 - nm));
              aiTimerRef.current = setTimeout(() => {
                setLastFlipped([first, second]); setFlipped([]);
                if (nm2.length === EMOJIS.length) {
                  setDone(true); getAiComment(myMoves, nm);
                  setShowResults({ myScore: myMoves, partnerScore: nm });
                } else {
                  aiTimerRef.current = setTimeout(() => { setLastFlipped([]); doAiTurn(nm2, currentCards); }, 800);
                }
              }, 700);
            } else {
              setStatusMsg(nl ? 'Geen paar — jouw beurt!' : 'No match — your turn!');
              aiTimerRef.current = setTimeout(() => {
                setLastFlipped([first, second]); setFlipped([]);
                aiTimerRef.current = setTimeout(() => {
                  setLastFlipped([]); setMyTurn(true); setStatusMsg('');
                }, 900);
              }, 900);
            }
            return nm;
          });
        }, 700);
      }, 700);
    }, 900);
  }, [nl, myMoves]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── PLAYER FLIPS A CARD ──────────────────────────────────────────
  const flip = async (idx) => {
    // In simultaneous mode with real friend: both can always flip (ignore myTurn)
    const canFlipNow = !isTurnBased || myTurn;
    if (locked || !canFlipNow || flipped.length === 2 || matched.includes(cards[idx]?.emoji) || flipped.includes(idx) || partnerFlipped.includes(idx)) return;
    const nf = [...flipped, idx];
    setFlipped(nf);

    if (nf.length === 2) {
      setLocked(true);
      const nm = myMoves + 1;
      setMyMoves(nm);
      const isMatch = cards[nf[0]].emoji === cards[nf[1]].emoji;

      if (isRealFriend && token) {
        // Push state to server for sync
        const newMatched = isMatch ? [...matched, cards[nf[0]].emoji] : matched;
        const newDone    = newMatched.length === EMOJIS.length;
        const invMoves   = amInviter ? nm : partnerMoves;
        const joiMoves   = amInviter ? partnerMoves : nm;
        // Turn-based: pass turn on miss; simultaneous: keep inviter (doesn't matter, poll ignores turn in simul)
        const nextTurn   = isTurnBased ? (isMatch ? myRole : partnerRole) : myRole;

        const newState = {
          type: 'memory', phase: 'playing',
          turn: newDone ? myRole : nextTurn,
          cards, matched: newMatched,
          inviterMoves: invMoves, joinerMoves: joiMoves,
          lastAction: { by: myRole, idx1: nf[0], idx2: nf[1], isMatch, ts: Date.now() },
          done: newDone,
        };

        setTimeout(async () => {
          if (isMatch) {
            matchedRef.current = newMatched;
            setMatched(newMatched);
            setFlipped([]);
            setLastFlipped([nf[0], nf[1]]);
            setTimeout(() => setLastFlipped([]), 900);
          } else {
            setFlipped([]);
            setLastFlipped([nf[0], nf[1]]);
            setTimeout(() => setLastFlipped([]), 1000);
            if (isTurnBased) {
              setMyTurn(false);
              setStatusMsg(nl ? `Wachten op ${partner?.name}...` : `Waiting for ${partner?.name}...`);
            }
          }
          setLocked(false);
          await updateGameState(token, newState);
          if (newDone) {
            setDone(true);
            getAiComment(nm, amInviter ? joiMoves : invMoves);
            setShowResults({ myScore: nm, partnerScore: amInviter ? joiMoves : invMoves });
          }
        }, isMatch ? 400 : 700);

      } else {
        // AI / solo
        setTimeout(() => {
          if (isMatch) {
            const nm2 = [...matched, cards[nf[0]].emoji];
            setMatched(nm2);
            saveGameScore('memory', Math.max(1, 100 - nm));
            if (!best || nm < best) setBest(nm);
            setFlipped([]); setLocked(false);
            if (nm2.length === EMOJIS.length) {
              setDone(true);
              if (partner) { getAiComment(nm, aiMoves); setShowResults({ myScore: nm, partnerScore: aiMoves }); }
            } else if (partner && mode === 'turnbased') {
              aiTimerRef.current = setTimeout(() => doAiTurn(nm2, cards), 600);
            }
          } else {
            if (partner && mode === 'turnbased') {
              aiTimerRef.current = setTimeout(() => { setFlipped([]); setLocked(false); doAiTurn(matched, cards); }, 900);
            } else {
              setFlipped([]); setLocked(false);
            }
          }
        }, isMatch ? 400 : 800);
      }
    }
  };

  // ── Results ──────────────────────────────────────────────────────
  if (showResults) {
    const pScore = showResults.partnerScore;
    const won    = showResults.myScore <= pScore;
    return (
      <MultiplayerResults lang={lang}
        myScore={showResults.myScore} myLabel={nl ? 'zetten' : 'moves'}
        partnerId={partner?.id} partnerScore={pScore} partnerLabel={nl ? 'zetten' : 'moves'}
        aiComment={aiComment || (won
          ? (nl ? 'Geweldig, minder zetten!' : 'Amazing, fewer moves!')
          : (nl ? 'Goed geprobeerd! Blijf oefenen.' : 'Well played! Keep practicing.'))}
        onClose={() => { setShowResults(null); setPartner(null); startLocal(); }}
        onRematch={() => { setShowResults(null); isRealFriend ? startServer() : startLocal(); }}
      />
    );
  }

  const waitingForServer = isRealFriend && !syncReady;

  return (
    <div>
      {showInvite && (
        <MultiplayerInviteModal lang={lang} title="Memory" icon="🧠"
          allUsers={allUsers} friends={friends}
          onInvite={(user, m) => { setPartner(user); setMode(m); setShowInvite(false); }}
          onClose={() => setShowInvite(false)} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}>← {nl ? 'Terug' : 'Back'}</button>
        <button onClick={() => setShowInvite(true)} style={s_GB({ fontSize: 11, padding: '5px 12px', borderRadius: 10 })}>👥 {nl ? 'Samen' : 'Together'}</button>
      </div>

      {/* Waiting for server to load deck */}
      {waitingForServer && (
        <div style={{ ...s_GC({ padding: 28, textAlign: 'center' }), marginBottom: 10 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>⏳</div>
          <div style={{ color: C.silver, fontSize: 13 }}>{nl ? 'Wachten tot het spel begint...' : 'Waiting for the game to start...'}</div>
        </div>
      )}

      {/* Turn banner (turn-based with partner) */}
      {isTurnBased && !waitingForServer && (
        <TurnBanner
          myName={userName}
          partnerName={partner?.name || ''}
          partner={partner}
          myTurn={myTurn}
          myScore={myMoves}
          partnerScore={isRealFriend ? partnerMoves : aiMoves}
          scoreLabel={nl ? 'zetten' : 'moves'}
          statusMsg={statusMsg}
          lang={lang}
        />
      )}

      {/* Simultaneous: show both scores */}
      {isRealFriend && !isTurnBased && !waitingForServer && (
        <div style={{ ...s_GC({ padding: '10px 16px', marginBottom: 10 }), display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: _T.accent, fontWeight: 700, marginBottom: 2 }}>{nl ? 'Jij' : 'You'}</div>
            <div style={{ fontSize: 20, color: C.white, fontWeight: 700 }}>{myMoves}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: C.silver, marginBottom: 2 }}>PAIRS</div>
            <div style={{ fontSize: 20, color: _T.accent, fontWeight: 700 }}>{matched.length}/{EMOJIS.length}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: C.silver, fontWeight: 700, marginBottom: 2 }}>{partner?.name}</div>
            <div style={{ fontSize: 20, color: C.teal, fontWeight: 700 }}>{partnerMoves}</div>
          </div>
        </div>
      )}

      {/* Score bar solo/AI */}
      {!isTurnBased && !isRealFriend && (
        <div style={{ ...s_GC({ padding: '10px 16px', marginBottom: 10 }), display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 13, color: C.silver }}>MOVES</div><div style={{ fontSize: 22, color: C.white, fontWeight: 700 }}>{myMoves}</div></div>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 13, color: C.silver }}>PAIRS</div><div style={{ fontSize: 22, color: _T.accent, fontWeight: 700 }}>{matched.length}/{EMOJIS.length}</div></div>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 13, color: C.silver }}>BEST</div><div style={{ fontSize: 22, color: C.teal, fontWeight: 700 }}>{best || '–'}</div></div>
        </div>
      )}

      {done && !showResults ? (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🎉</div>
          <div style={{ color: C.white, fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{myMoves} {nl ? 'zetten!' : 'moves!'}</div>
          {best === myMoves && <div style={{ color: C.teal, fontSize: 13, marginBottom: 12 }}>🏆 {nl ? 'Nieuw record!' : 'New record!'}</div>}
          <button style={s_PB()} onClick={isRealFriend ? startServer : startLocal}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      ) : !waitingForServer && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 6 }}>
          {cards.map((c, i) => {
            const isMatched       = matched.includes(c.emoji);
            const isMyFlipped     = flipped.includes(i);
            const isPartnerFlip   = partnerFlipped.includes(i);
            const isMyLast        = lastFlipped.includes(i);
            const isPartnerLast   = partnerLast.includes(i);
            const show            = isMyFlipped || isMatched || isMyLast || isPartnerFlip || isPartnerLast;
            const canFlipNow      = !isTurnBased || myTurn;
            const canFlip         = canFlipNow && !locked && !isMatched && !isMyFlipped && !isPartnerFlip;
            const isActive        = isMyFlipped;
            const isPartnerActive = isPartnerFlip;

            return (
              <div key={c.id} onClick={() => canFlip && flip(i)} style={{
                aspectRatio: '1',
                background: isMatched
                  ? `${_T.accent}25`
                  : isActive
                  ? _T.bg4
                  : isPartnerActive
                  ? `${C.teal}18`
                  : (isMyLast || isPartnerLast) ? '#2A5A8820' : _T.bg4,
                border: `1.5px solid ${
                  isMatched ? _T.accent
                  : isActive ? '#5566aa'
                  : isPartnerActive ? C.teal
                  : (isMyLast || isPartnerLast) ? '#2A9E8266'
                  : _T.cardBorderBot
                }`,
                borderRadius: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: show ? 20 : 10,
                cursor: canFlip ? 'pointer' : 'default',
                transition: 'all 0.25s', userSelect: 'none',
                opacity: (isTurnBased && !myTurn && !isMatched && !isMyFlipped && !isPartnerFlip && !isMyLast && !isPartnerLast) ? 0.65 : 1,
              }}>
                {show ? c.emoji : '·'}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MemoryGame;
